import React, { FC, memo, useCallback } from 'react';
import { useTranslation } from 'services/localization';
import { selectItemsByGroupAndDeployment } from '../../../../preferencesSelectors';
import { sendItemsFromUI } from '../../../../preferencesSlice';
import { ContentHeader } from '../ContentHeader';
import { ItemCard } from '../ItemCard';
import { TriState } from '../../../../types';
import { GroupType } from '../../../../../../services/api';
import { trackSubscriptionGroupUpdate } from '../../../../services/userActionEvents';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import { Box, Grid, media, xcss } from '@atlaskit/primitives';

const itemCardGridStyles = xcss({
  [media.above.xxs]: { gridTemplateColumns: 'repeat(1, 1fr)' },
  [media.above.sm]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [media.above.md]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
});

interface OwnProps {
  type: GroupType;
  groupKey: string;
  tabKey: string;
}

const GroupContent: FC<OwnProps> = ({ type, groupKey, tabKey }) => {
  const dispatch = useAppDispatch();
  const { translate, keyWithTitle, keyWithDescription } = useTranslation();
  const items = useAppSelector(selectItemsByGroupAndDeployment(groupKey, tabKey));

  const handleSelectAll = useCallback(() => {
    if (type === GroupType.SUBSCRIPTIONS && groupKey !== 'product_recommendations') {
      trackSubscriptionGroupUpdate(true, { groupName: groupKey, tabName: tabKey });
    }
    dispatch(
      sendItemsFromUI({
        keys: items,
        state: TriState.true,
        bypassConsentCheck: false,
        bypassConsentDeclinePrCheck: false,
        skipNotification: false,
        sourceGroup: groupKey,
        sourceTab: tabKey,
        sourceComponent: 'GroupContent',
      }),
    );
  }, [dispatch, items, groupKey, type, tabKey]);

  const handleSelectNone = useCallback(() => {
    if (type === GroupType.SUBSCRIPTIONS) {
      trackSubscriptionGroupUpdate(false, { groupName: groupKey, tabName: tabKey });
    }
    dispatch(
      sendItemsFromUI({
        keys: items,
        state: TriState.false,
        bypassConsentCheck: false,
        bypassConsentDeclinePrCheck: false,
        skipNotification: false,
        sourceGroup: groupKey,
        sourceComponent: 'GroupContent',
      }),
    );
  }, [dispatch, items, type, groupKey, tabKey]);

  return (
    <Box>
      <ContentHeader
        text={translate(keyWithDescription(groupKey))}
        onClickAll={handleSelectAll}
        onClickNone={handleSelectNone}
        groupKey={groupKey}
      />
      <Grid xcss={itemCardGridStyles} gap={'space.200'}>
        {[...items]
          .sort((left, right) => {
            return translate(keyWithTitle(left)).localeCompare(translate(keyWithTitle(right)));
          })
          .map((itemKey) => {
            return <ItemCard type={type} key={itemKey} itemKey={itemKey} groupKey={groupKey} tabKey={tabKey} />;
          })}
      </Grid>
    </Box>
  );
};

export default memo(GroupContent);
